import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { setAuthToken, clearAuthToken, getAuthToken } from '../api';

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/api/auth/me/');
      if (response.data.authenticated) {
        setUser(response.data);
      } else {
        // Not authenticated, clear any stale token
        clearAuthToken();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearAuthToken();
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, redirect = '') => {
    const response = await api.post('/api/auth/register/', { email, redirect });
    // If auto-logged in, save the auth token
    if (response.data.auth_token) {
      setAuthToken(response.data.auth_token);
      if (response.data.user) {
        setUser({ authenticated: true, ...response.data.user });
      }
    }
    return response.data;
  };

  const loginWithToken = async (token) => {
    const response = await api.post('/api/auth/login/', { token });
    // Save auth token for mobile browsers
    if (response.data.auth_token) {
      setAuthToken(response.data.auth_token);
    }
    if (response.data.user) {
      setUser({ authenticated: true, ...response.data.user });
    }
    return response.data;
  };

  const logout = async () => {
    await api.post('/api/auth/logout/', {});
    clearAuthToken();
    setUser(null);
  };

  const value = {
    user,
    loading,
    register,
    loginWithToken,
    logout,
    isAuthenticated: !!user?.authenticated,
    hasAuthToken: !!getAuthToken()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
